<template>
    <div class="main-info">
        <div class="main">
            <div class="form-field required">
                <div class="title">Название продукта</div>
                <div class="value">
                    <input v-model="v.state.name.$model"
                           :class="{ error: v.state.name.$error }"
                           data-test-id="input-name"
                           placeholder="Введите название"
                           type="text"
                    />
                </div>
            </div>
            <div class="form-field required">
                <div class="title">Обзор продукта</div>
                <div class="value">
                    <wysiwyg v-model.trim="v.state.description.$model"
                             :class="{ error: v.state.description.$error }"
                             data-test-id="textarea-description"
                             placeholder="Опишите состав продуктового решения, его технические особенности. Приведите примеры решаемых задач"
                    />
                </div>
            </div>
            <div class="form-field required">
                <div class="title">Обзор рынка</div>
                <div class="value">
                    <wysiwyg v-model.trim="v.state.marketOverview.$model"
                             :class="{ error: v.state.marketOverview.$error }"
                             data-test-id="textarea-description"
                             placeholder="Опишите отрасль компании-заказчика или сферу деятельности бизнеса, где можно применить данное решение"
                    />
                </div>
            </div>
            <div class="form-field required">
                <div class="title">Целевая аудитория</div>
                <div class="value">
                    <wysiwyg v-model.trim="v.state.targetAudience.$model"
                             :class="{ error: v.state.targetAudience.$error }"
                             data-test-id="textarea-description"
                             placeholder="Опишите профиль клиента, с которым нужно взаимодействовать при продаже продукта (например, ИБ, HR). Укажите возможных ЛПР/ЛВПР"
                    />
                </div>
            </div>
            <div class="form-field required">
                <div class="title">Скрипт продажи</div>
                <div class="value">
                    <wysiwyg v-model.trim="v.state.salesScript.$model"
                             :class="{ error: v.state.salesScript.$error }"
                             data-test-id="textarea-description"
                             placeholder="Скрипт, определяющий потенциальную потребность в продукте"
                    />
                </div>
            </div>
            <div class="form-field required">
                <div class="title">Отрасли продукта</div>
                <div class="value">
                    <Multiselect v-model="v.state.industries.$model"
                                 :class="{ error: v.state.industries.$error }"
                                 :options="industries"
                                 :toTitle="it => it.name"
                                 class="industries product-tag"
                                 data-test-id="combobox-industries"
                    />
                </div>
            </div>
            <div class="form-field">
                <div class="title">Теги</div>
                <div class="value">
                    <TagSelect v-model="state.tags"
                               :append="onEditTag"
                               :resolve="loadInput"
                               :search="searchWithKindGroup"
                               :show-append="true"
                               :toTitle="it => '#' + it.name"
                               class="tag-select"
                               @addSynonym="onEditTag($event)"
                    >
                        <template #append>
                            <button class="primary no-border iconed">
                                <IconPlusLarge class="icon" />
                                <span class="text">Добавить новый тег</span>
                            </button>
                        </template>
                    </TagSelect>
                </div>
            </div>

            <div class="grid-layout">
                <div class="row">
                    <div class="col col-6">
                        <div class="form-field required">
                            <div class="title">Блок</div>
                            <div class="value">
                                <Combobox v-model="v.state.cluster.$model"
                                          :class="{ error: v.state.cluster.$error }"
                                          :disabled="clusterDisabled"
                                          :resolve="resolveCluster"
                                          :search="searchClusterAvailable"
                                          :toTitle="it => it.name"
                                          @input="changeCluster"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="col col-6">
                        <div class="form-field required">
                            <div class="title">Направление</div>
                            <div class="value">
                                <Combobox :key="state.cluster"
                                          v-model="v.state.office.$model"
                                          :class="{ error: v.state.office.$error }"
                                          :disabled="officeDisabled || !state.cluster"
                                          :resolve="resolveOffice"
                                          :search="searchOffice"
                                          :toTitle="it => it.name"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col col-6">
                        <div class="form-field">
                            <div class="title">Целевой сегмент</div>
                            <div class="value">
                                <Combobox v-model="state.segment"
                                          :options="segments"
                                          :toTitle="it => it.name"
                                          data-test-id="combobox-segment"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="col col-6">
                        <div class="form-field required">
                            <div class="title">Тип продукта</div>
                            <div class="value">
                                <Combobox v-model="v.state.type.$model"
                                          :class="{ error: v.state.type.$error }"
                                          :options="productTypes"
                                          :toTitle="it => it.name"
                                          data-test-id="combobox-type"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <EditTagModal ref="editTagModal" />
    </div>
</template>

<script>
import EditTagModal from '@/common/page_comp/Tag/EditTagModal.vue';
import TagSelect from '@/common/page_comp/Tag/select/TagSelect.vue';
import { updateState } from '@/common/util/util.js';
import { nameRulesDefault } from '@/common/util/validations/name.js';
import { resolveCluster, searchClusterAvailable } from '@/restapi/cluster.js';
import { resolveOffice, searchOffice } from '@/restapi/office.js';
import { loadInput, loadTag, searchWithKindGroup } from '@/restapi/tag/tag.js';
import { showNotice } from '@dsee/components/popups/message.js';
import { useVuelidate } from '@vuelidate/core';
import { minLength, required } from '@vuelidate/validators';

export default {
    components: { EditTagModal, TagSelect },
    props: {
        value: {
            type: Object,
            required: true,
        },
        dicts: {
            type: Object,
            required: true,
        },
        clusterDisabled: {
            type: Boolean,
            default: false,
        },
        officeDisabled: {
            type: Boolean,
            default: false,
        },
    },
    setup() {
        return {
            v: useVuelidate(),
        };
    },
    validations() {
        return {
            state: {
                name: {
                    ...nameRulesDefault(),
                },
                description: { required },
                targetAudience: { required },
                marketOverview: { required },
                salesScript: { required },
                type: { required },
                industries: {
                    required,
                    minLength: minLength(1),
                },
                office: { required },
                cluster: { required },
            },
        };
    },
    computed: {
        productTypes() {
            return this.dicts.productTypes ?? [];
        },
        industries() {
            return this.dicts.industries ?? [];
        },
        tags() {
            return this.dicts.tags ?? [];
        },
        segments() {
            return this.dicts.segments ?? [];
        },
    },
    data() {
        return {
            tmpId: 0,

            state: {
                name: '',
                description: '',
                targetAudience: '',
                marketOverview: '',
                salesScript: '',
                type: null,
                industries: [],
                tags: [],
                office: null,
                cluster: null,
                segment: null,
            },
        };
    },
    created() {
        this.setInnerState(this.value);
    },
    methods: {
        resolveCluster,
        searchClusterAvailable,
        resolveOffice,
        async searchOffice(query) {
            return await searchOffice(query, this.state.cluster);
        },
        searchWithKindGroup,
        loadInput,
        setInnerState(state) {
            updateState(this.state, state);
        },
        async onEditTag(tag) {
            if (tag)
                tag = await loadTag(tag.id);

            const resp = await this.$refs.editTagModal.open(tag);
            if (!resp)
                return false;

            return resp;
        },
        async changeCluster() {
            this.state.office = null;
        },
    },
    watch: {
        state: {
            deep: true,
            handler(value) {
                this.$emit('input', Object.assign({}, value));
            },
        },
        value: {
            deep: true,
            handler(value) {
                this.setInnerState(value);
            },
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~@dsee/mixins';

.main-info {
    @include scrollbar;

    .option {
        .link {
            color: var(--label-color);
            font-size: 12px;
        }
    }

    .main {
        margin-bottom: 16px;

        .row {
            margin-top: 20px;
        }
    }
}
</style>
